/**
 * User management and settings storage service
 *
 * Handles user profile data, settings, and preferences with coordinate system integration.
 * Implements offline-first patterns for user data management.
 */

import { db } from '../database/schema.js'
import type { User, UserSettings } from '../database/schema.js'
import {
  OfflineFirstStorageService,
  type StorageResult,
  type QueryOptions,
} from './base-storage.js'
import { validateUser, validateUserSettings, runValidation } from './validation.js'
import { DEFAULT_PREFERENCES, type UserPreferences } from '../lib/coordinates.js'

/**
 * Settings that stay DEVICE-LOCAL: never persisted to the synced `users.settings`
 * record, so they don't propagate across devices. They live only in the localStorage
 * settings mirror (`SettingsStorage`). Used to strip them before push and to preserve
 * them on pull.
 */
export const DEVICE_LOCAL_SETTINGS_KEYS = ['syncPreferences'] as const

/**
 * User profile with enhanced settings
 */
export interface UserProfile extends User {
  preferences?: UserPreferences | undefined
  lastLoginAt?: string | undefined
  isFirstTime?: boolean | undefined
}

/**
 * User creation data
 */
export interface CreateUserData {
  email: string
  displayName: string
  provider: 'google' | 'apple' | 'email'
  bio?: string | undefined
  certifications?: string[] | undefined
  settings?: Partial<UserSettings> | undefined
}

/**
 * User update data
 */
export interface UpdateUserData {
  displayName?: string | undefined
  givenName?: string | undefined
  familyName?: string | undefined
  picture?: string | undefined
  phone?: string | undefined
  emergencyContact?:
    | {
        name: string
        phone: string
        relationship: string
      }
    | undefined
  bio?: string | undefined
  certifications?: string[] | undefined
  location?:
    | {
        region: string
        coordinates: [number, number]
        lastUpdated: string
      }
    | undefined
}

/**
 * User storage service implementing offline-first patterns
 */
export class UserStorageService extends OfflineFirstStorageService<User> {
  constructor() {
    super(db.users, 'user')
  }

  /**
   * Create a new user with validation and default settings
   */
  async createUser(userData: CreateUserData): Promise<StorageResult<UserProfile>> {
    // Validate user data
    const validation = runValidation(validateUser, userData as Partial<User>, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.executeOperation(async () => {
      const id = this.generateId()
      const now = new Date().toISOString()

      // Create default settings merged with provided settings
      const defaultSettings: UserSettings = {
        theme: 'auto',
        coordinateFormat: DEFAULT_PREFERENCES.coordinateFormat,
        unitSystem: DEFAULT_PREFERENCES.unitSystem,
        elevationUnit: DEFAULT_PREFERENCES.elevationUnit,
        distanceUnit: DEFAULT_PREFERENCES.distanceUnit,
        areaUnit: DEFAULT_PREFERENCES.areaUnit,
        temperatureUnit: DEFAULT_PREFERENCES.temperatureUnit,
        syncPreferences: {
          wifi: true,
          cellular: false,
        },
        notifications: {
          emergency: true,
          messages: true,
        },
        privacy: {
          shareLocation: 'approximate',
        },
        ...userData.settings,
      }

      const user: User = {
        id,
        email: userData.email,
        displayName: userData.displayName,
        provider: userData.provider,
        bio: userData.bio,
        certifications: userData.certifications,
        settings: defaultSettings,
        createdAt: now,
      }

      await this.table.add(user)

      const userProfile: UserProfile = {
        ...user,
        preferences: DEFAULT_PREFERENCES,
        isFirstTime: true,
      }

      return userProfile
    }, 'create user')
  }

  /**
   * Update user profile
   */
  async updateUser(id: string, updates: UpdateUserData): Promise<StorageResult<UserProfile>> {
    // Validate updates
    const validation = runValidation(validateUser, updates as Partial<User>, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    // Route through the tracked update() so the change records a sync event and is
    // pushed to the backend (user.settings/profile syncs cross-device). LWW: the user
    // entity is never version-gated on the server.
    const result = await this.update(id, updates as Partial<User>)
    if (!result.success || !result.data) {
      return { success: false, error: result.error }
    }
    const userProfile: UserProfile = {
      ...result.data,
      preferences: this.extractPreferences(result.data.settings),
    }
    return { success: true, data: userProfile }
  }

  /**
   * Update user settings
   */
  async updateSettings(
    userId: string,
    settings: Partial<UserSettings>
  ): Promise<StorageResult<UserSettings>> {
    // Validate settings
    const validation = runValidation(validateUserSettings, settings, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    const existing = await this.table.get(userId)
    // Dexie users.settings is the SYNCED subset only — strip device-local keys so they
    // never reach the backend (they live in the localStorage settings mirror).
    const merged: Record<string, unknown> = { ...existing?.settings, ...settings }
    for (const k of DEVICE_LOCAL_SETTINGS_KEYS) delete merged[k]
    const updatedSettings = merged as UserSettings

    // Route through the tracked update() so the change records a sync event and pushes.
    const result = await this.update(userId, { settings: updatedSettings })
    return result.success
      ? { success: true, data: updatedSettings }
      : { success: false, error: result.error }
  }

  /**
   * Get user profile with preferences
   */
  async getUserProfile(id: string): Promise<StorageResult<UserProfile | undefined>> {
    return this.executeOperation(async () => {
      const user = await this.table.get(id)
      if (!user) {
        return undefined
      }

      const userProfile: UserProfile = {
        ...user,
        preferences: this.extractPreferences(user.settings),
        lastLoginAt: new Date().toISOString(),
      }

      return userProfile
    }, 'get user profile')
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email: string): Promise<StorageResult<User | undefined>> {
    return this.executeOperation(async () => {
      const users = await this.table.where('email').equals(email).toArray()
      return users.length > 0 ? users[0] : undefined
    }, 'get user by email')
  }

  /**
   * Update user location
   */
  async updateLocation(
    userId: string,
    region: string,
    coordinates: [number, number]
  ): Promise<StorageResult<User>> {
    return this.executeOperation(async () => {
      const existing = await this.table.get(userId)
      if (!existing) {
        throw new Error('User not found')
      }

      const now = new Date().toISOString()
      const updatedUser = {
        ...existing,
        location: {
          region,
          coordinates,
          lastUpdated: now,
        },
        updatedAt: now,
      }

      await this.table.put(updatedUser)
      return updatedUser
    }, 'update user location')
  }

  /**
   * Add certification to user
   */
  async addCertification(userId: string, certification: string): Promise<StorageResult<string[]>> {
    return this.executeOperation(async () => {
      const existing = await this.table.get(userId)
      if (!existing) {
        throw new Error('User not found')
      }

      const certifications = existing.certifications ?? []
      if (!certifications.includes(certification)) {
        certifications.push(certification)

        const updatedUser = {
          ...existing,
          certifications,
          updatedAt: new Date().toISOString(),
        }

        await this.table.put(updatedUser)
      }

      return certifications
    }, 'add certification')
  }

  /**
   * Remove certification from user
   */
  async removeCertification(
    userId: string,
    certification: string
  ): Promise<StorageResult<string[]>> {
    return this.executeOperation(async () => {
      const existing = await this.table.get(userId)
      if (!existing) {
        throw new Error('User not found')
      }

      const certifications = (existing.certifications ?? []).filter(cert => cert !== certification)

      const updatedUser = {
        ...existing,
        certifications,
        updatedAt: new Date().toISOString(),
      }

      await this.table.put(updatedUser)
      return certifications
    }, 'remove certification')
  }

  /**
   * Search users by display name or email
   */
  async searchUsers(query: string, options: QueryOptions = {}): Promise<StorageResult<User[]>> {
    return this.executeOperation(async () => {
      const searchTerm = query.toLowerCase()

      let users = await this.table
        .toCollection()
        .filter(
          user =>
            user.displayName.toLowerCase().includes(searchTerm) ||
            user.email.toLowerCase().includes(searchTerm)
        )
        .toArray()

      // Apply limit if specified
      if (options.limit) {
        users = users.slice(0, options.limit)
      }

      return users
    }, 'search users')
  }

  /**
   * Get users with specific certifications
   */
  async getUsersWithCertification(certification: string): Promise<StorageResult<User[]>> {
    return this.executeOperation(async () => {
      const users = await this.table
        .toCollection()
        .filter(user => !!user.certifications?.includes(certification))
        .toArray()

      return users
    }, 'get users with certification')
  }

  /**
   * Get users in a region
   */
  async getUsersInRegion(region: string): Promise<StorageResult<User[]>> {
    return this.executeOperation(async () => {
      const users = await this.table
        .toCollection()
        .filter(user => !!user.location?.region.toLowerCase().includes(region.toLowerCase()))
        .toArray()

      return users
    }, 'get users in region')
  }

  /**
   * Export user data for backup or migration
   */
  async exportUserData(userId: string): Promise<StorageResult<UserProfile>> {
    return this.executeOperation(async () => {
      const user = await this.table.get(userId)
      if (!user) {
        throw new Error('User not found')
      }

      const userProfile: UserProfile = {
        ...user,
        preferences: this.extractPreferences(user.settings),
      }

      return userProfile
    }, 'export user data')
  }

  /**
   * Clear user data (for logout/reset)
   */
  async clearUserData(userId: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      await this.table.delete(userId)
      return undefined
    }, 'clear user data')
  }

  /**
   * Extract user preferences from settings for coordinate formatting
   */
  private extractPreferences(settings?: UserSettings): UserPreferences {
    if (!settings) {
      return DEFAULT_PREFERENCES
    }

    return {
      coordinateFormat: settings.coordinateFormat,
      unitSystem: settings.unitSystem,
      elevationUnit: settings.elevationUnit,
      distanceUnit: settings.distanceUnit,
      areaUnit: settings.areaUnit,
      temperatureUnit: settings.temperatureUnit,
    }
  }

  /**
   * Validate and update coordinate format preference
   */
  async updateCoordinateFormat(
    userId: string,
    format: UserSettings['coordinateFormat']
  ): Promise<StorageResult<UserSettings>> {
    return this.updateSettings(userId, { coordinateFormat: format })
  }

  /**
   * Validate and update unit system preference
   */
  async updateUnitSystem(
    userId: string,
    unitSystem: UserSettings['unitSystem']
  ): Promise<StorageResult<UserSettings>> {
    return this.updateSettings(userId, { unitSystem })
  }

  /**
   * Update privacy settings
   */
  async updatePrivacySettings(
    userId: string,
    privacy: Partial<UserSettings['privacy']>
  ): Promise<StorageResult<UserSettings>> {
    const existingResult = await this.getById(userId)
    if (!existingResult.success || !existingResult.data) {
      return {
        success: false,
        error: this.createError('USER_NOT_FOUND', 'User not found'),
      }
    }

    const currentPrivacy = existingResult.data.settings?.privacy ?? { shareLocation: 'none' }
    const updatedPrivacy = { ...currentPrivacy, ...privacy }

    return this.updateSettings(userId, { privacy: updatedPrivacy })
  }

  /**
   * Update notification settings
   */
  async updateNotificationSettings(
    userId: string,
    notifications: Partial<UserSettings['notifications']>
  ): Promise<StorageResult<UserSettings>> {
    const existingResult = await this.getById(userId)
    if (!existingResult.success || !existingResult.data) {
      return {
        success: false,
        error: this.createError('USER_NOT_FOUND', 'User not found'),
      }
    }

    const currentNotifications = existingResult.data.settings?.notifications ?? {
      emergency: true,
      messages: true,
    }
    const updatedNotifications = { ...currentNotifications, ...notifications }

    return this.updateSettings(userId, { notifications: updatedNotifications })
  }
}

// Create and export the singleton instance
export const userStorage = new UserStorageService()
